import React, {useEffect} from 'react';

import {
  Breakpoints,
  ButtonAppearance,
  ButtonCustom,
  buttonPaddingGenerator,
  useIsMobile,
} from '@vw-marketing/us-components';

import {Breadcrumb} from '../breadcrumb';
import {isOnlineBuyingLinkModified} from '../../utils/general';
import {setBreadcrumbs, updateUrlFilters} from '../../utils/navigation-url';
import {StyledButtonsWrapper, StyledHeaderContainer} from './styled';
import {UsedVehicle} from '../../typing/used-inventory';
import {useFeatureAppConfig} from '../../hooks/use-feature-app-config';
import {useFeatureServices} from '../../hooks/use-feature-services';
import {useHistory} from '../../hooks/use-history';
import {useReactiveVar} from '@apollo/client';
import {
  userFilters,
  hasBeforeResults,
  globalIsSticky,
} from '../../store/local-storage';
import {useTranslations} from '../../hooks/use-translations';

interface DetailsHeaderProps {
  vehicle: UsedVehicle;
  onHandleContactDealer?: () => void;
  onHandleMakeOffer?: () => void;
  startBuyingHandler: (name: string) => void;
}

export const DetailsHeader = ({
  vehicle,
  onHandleContactDealer,
  onHandleMakeOffer,
  startBuyingHandler,
}: DetailsHeaderProps): JSX.Element => {
  const isSticky = useReactiveVar(globalIsSticky);
  const isMobile = useIsMobile(Breakpoints.b560);
  const {
    detailsSection: {header},
  } = useTranslations();
  const history = useHistory();
  const {landingPageName = 'cpo-landing'} = useFeatureAppConfig();
  const {
    'breadcrumb-service': breadcrumbService,
    'navigation-service': navigationService,
  } = useFeatureServices();
  const hasResultsLoaded = hasBeforeResults();

  const breadcrumbHandler = (): void => {
    // Keep user's selected filters
    if (hasResultsLoaded) {
      window.history.back();
    } else {
      const href = '/';
      const userSelectedFilters = userFilters();
      const queryParams = updateUrlFilters(history, userSelectedFilters, true);
      history.push(`${href}?${queryParams}`);
    }
  };
  setBreadcrumbs(
    history.location,
    history,
    vehicle,
    breadcrumbService,
    navigationService,
    landingPageName,
  );

  useEffect(() => {
    // Workaround in order to hide breadcrumbs component added by the CMS
    let breadcrumbElement = document.querySelector(
      '[aria-label="Breadcrumbs"]',
    ) as HTMLElement;

    if (!breadcrumbElement) return;

    if (isSticky || isMobile) {
      breadcrumbElement.style.display = 'none';
    } else {
      breadcrumbElement.style.display = 'block';
    }
  }, [isSticky]);

  useEffect(() => {
    return () => {
      let breadcrumbElement = document.querySelector(
        '[aria-label="Breadcrumbs"]',
      ) as HTMLElement;
      if (!breadcrumbElement) return;
      breadcrumbElement.style.display == 'none' &&
        (breadcrumbElement.style.display = 'block');
    };
  }, []);

  const breadcrumbLabel = isMobile && isSticky ? '' : header.breadcrumbLabel;

  return (
    <StyledHeaderContainer
      className="details-header_container"
      isSticky={isSticky}
    >
      <div className="breadcrumb-wrapper">
        <Breadcrumb
          label={breadcrumbLabel}
          breadcrumbEvent={breadcrumbHandler}
          iconSize={24}
        />
      </div>
      {isSticky ? (
        <StyledButtonsWrapper>
          {/* @ts-ignore */}
          <ButtonCustom
            appearance={ButtonAppearance.Secondary}
            onClick={onHandleContactDealer}
            padding={buttonPaddingGenerator(12, 10, 10, 21)}
            stretchContent={true}
          >
            {header.contactDealerButtonLabel}
          </ButtonCustom>
          {/* @ts-ignore */}
          <ButtonCustom
            appearance={ButtonAppearance.Primary}
            onClick={
              isOnlineBuyingLinkModified(vehicle)
                ? startBuyingHandler.bind(null, 'Build my Deal')
                : onHandleMakeOffer
            }
            padding={buttonPaddingGenerator(12, 18, 18, 30)}
            stretchContent={true}
          >
            {isOnlineBuyingLinkModified(vehicle)
              ? header.buildMyDealLabel
              : header.makeAnOfferButtonLabel}
          </ButtonCustom>
        </StyledButtonsWrapper>
      ) : (
        <></>
      )}
    </StyledHeaderContainer>
  );
};
